import { GraduationCap, Megaphone, Globe2, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import Reveal from '@/components/Reveal';
import SEO from '@/components/SEO';
import { SectionLabel, PrimaryButton } from '@/components/Button';
import EngagementCard from '@/components/EngagementCard';
import { IMAGES } from '@/data/insights';

const ICONS = [GraduationCap, Megaphone, Globe2, Settings2];
const NUMBERS = ['01', '02', '03', '04'];

export default function Services() {
  const { t } = useTranslation(['services', 'common']);
  const blocks = t('blocks', { returnObjects: true });
  const engagements = t('how.engagements', { returnObjects: true });

  return (
    <Layout>
      <SEO title={t('seo.title')} description={t('seo.description')} />

      <section className="pt-32 md:pt-40 pb-16 md:pb-24" data-testid="services-hero">
        <div className="max-w-content mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <Reveal>
              <SectionLabel>{t('hero.eyebrow')}</SectionLabel>
              <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-[60px] font-semibold text-brand-primary leading-[1.06] tracking-tight">
                {t('hero.headline')}
              </h1>
              <p className="mt-7 text-[17px] md:text-lg text-brand-text leading-[1.8] max-w-[68ch]">
                {t('hero.body')}
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.08}>
              <div className="overflow-hidden aspect-[4/5]">
                <img
                  src={IMAGES.servicesSupporting}
                  alt={t('hero.imgAlt')}
                  loading="eager"
                  className="w-full h-full object-cover saturate-[0.9] transition-transform duration-[600ms] ease-out hover:scale-[1.04]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12" data-testid="services-blocks">
        <div className="max-w-content mx-auto px-6 md:px-8">
          {blocks.map((b, i) => (
            <ServiceBlockRow
              key={NUMBERS[i]}
              number={NUMBERS[i]}
              Icon={ICONS[i]}
              title={b.title}
              audience={b.audience}
              body={b.body}
              bullets={b.bullets}
              includesLabel={t('includesLabel')}
              talkCta={`${t('cta.talkToJonathan', { ns: 'common' })} →`}
              reverse={i % 2 === 1}
            />
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32 bg-brand-surface" data-testid="services-how">
        <div className="max-w-content mx-auto px-6 md:px-8">
          <Reveal>
            <SectionLabel>{t('how.label')}</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-[40px] font-semibold text-brand-primary leading-tight tracking-tight max-w-3xl">
              {t('how.headline')}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {engagements.map((e, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <EngagementCard title={e.title} body={e.body} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="services-cta">
        <div className="max-w-content mx-auto px-6 md:px-8 max-w-3xl">
          <Reveal>
            <SectionLabel>{t('cta.label')}</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-[44px] font-semibold text-brand-primary leading-tight tracking-tight">
              {t('cta.headline')}
            </h2>
            <p className="mt-6 text-[17px] text-brand-text leading-[1.8] max-w-[68ch]">{t('cta.body')}</p>
            <div className="mt-10">
              <PrimaryButton to="/contact" testId="services-cta-button">{t('cta.startConversation', { ns: 'common' })}</PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}

function ServiceBlockRow({ number, Icon, title, audience, body, bullets, includesLabel, talkCta, reverse = false }) {
  return (
    <Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start py-14 md:py-20 border-t border-black/10">
        <div className={`md:col-span-5 ${reverse ? 'md:order-2' : ''}`}>
          <div className="flex items-center gap-4">
            <Icon size={22} strokeWidth={1.25} className="text-brand-accent" />
            <p className="font-display text-[56px] md:text-[88px] leading-none font-semibold text-brand-accent/80 tracking-tighter">
              {number}
            </p>
          </div>
          <h3 className="mt-5 font-display text-2xl md:text-[30px] font-semibold text-brand-primary leading-tight tracking-tight">
            {title}
          </h3>
          <p className="mt-4 text-sm text-brand-muted italic">{audience}</p>
        </div>
        <div className={`md:col-span-7 ${reverse ? 'md:order-1' : ''}`}>
          <p className="text-[17px] text-brand-text leading-[1.8] max-w-[68ch]">{body}</p>
          <p
            className="text-[11px] md:text-[12px] uppercase font-medium text-brand-accent mt-8 mb-4"
            style={{ letterSpacing: '0.18em' }}
          >
            {includesLabel}
          </p>
          <ul className="space-y-2.5">
            {bullets.map((b, i) => (
              <li key={i} className="flex gap-3 text-[15px] text-brand-text leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-accent shrink-0" aria-hidden="true" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 mt-8 text-sm font-medium text-brand-accent link-underline"
            data-testid={`service-cta-${number}`}
          >
            {talkCta}
          </Link>
        </div>
      </div>
    </Reveal>
  );
}
